import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cartLineToItem, unitPrice, useCart, type CartLine, type Order } from '../store/cart';
import { CURRENCY, FREE_SHIPPING_FROM, PAYMENT_METHODS, SHIPPING_METHODS, byId } from '../data/catalog';
import { Button, Card, Money, inputCls } from '../components/ui';
import { useTitle } from '../components/Layout';

const STEPS = ['Dane i dostawa', 'Płatność', 'Podsumowanie'];

/**
 * Wspólny szkielet ecommerce (currency/value/coupon/items) dla
 * begin_checkout, add_shipping_info i add_payment_info — w tym pliku
 * powtarzałby się inaczej trzeci raz. `null`, gdy koszyk już opustoszał
 * (np. ktoś wrócił z historii przeglądarki po sfinalizowaniu zamówienia).
 */
function buildCheckoutEcommerce(lines: CartLine[], subtotal: number, coupon?: string) {
  const items = lines.map(cartLineToItem).filter((item): item is NonNullable<typeof item> => item !== null);
  if (items.length === 0) return null;
  return { currency: CURRENCY, value: subtotal, ...(coupon ? { coupon } : {}), items };
}

export function Checkout() {
  useTitle('Zamówienie');
  const cart = useCart();
  const nav = useNavigate();
  const leaving = useRef(false);

  const [step, setStep] = useState(1);
  const [ship, setShip] = useState(SHIPPING_METHODS[0]);
  const [pay, setPay] = useState(PAYMENT_METHODS[0]);
  const [form, setForm] = useState({
    email: cart.user?.email ?? '',
    name: '',
    street: '',
    zip: '',
    city: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cart.lines.length === 0 && !leaving.current) nav('/koszyk');
  }, [cart.lines.length, nav]);

  // begin_checkout — rekomendowane zdarzenie GA4, raz na OTWARCIE strony
  // zamówienia (ten sam wzorzec co view_cart: jeden fizyczny mount = jedno
  // zdarzenie, useRef(false) wystarcza, StrictMode-safe). Kupon dołączamy
  // na poziomie zdarzenia — to jedno z miejsc, gdzie `coupon` faktycznie
  // jest w oficjalnej specyfikacji GA4 (w odróżnieniu od view_cart).
  // Checkout w tej aplikacji nie ma własnego pola na kupon — cokolwiek
  // zastosowano na stronie koszyka, jest już ustalone w tym momencie.
  const beginCheckoutFired = useRef(false);
  useEffect(() => {
    if (beginCheckoutFired.current || cart.lines.length === 0) return;
    beginCheckoutFired.current = true;

    const ecommerce = buildCheckoutEcommerce(cart.lines, cart.subtotal, cart.coupon);
    if (!ecommerce) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: 'begin_checkout', ecommerce });
    // Celowo pusta tablica zależności — ten snapshot ma odzwierciedlać stan
    // w momencie wejścia na stronę zamówienia, nie każdą późniejszą zmianę.
  }, []);

  const shippingCost = cart.coupon === 'DARMOWA' || cart.subtotal >= FREE_SHIPPING_FROM ? 0 : ship.price;
  const total = Math.round((cart.subtotal - cart.discount + shippingCost) * 100) / 100;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.email.includes('@')) e.email = 'Podaj poprawny adres e-mail.';
    if (form.name.trim().length < 3) e.name = 'Podaj imię i nazwisko.';
    if (form.street.trim().length < 3) e.street = 'Podaj ulicę i numer.';
    if (!/^\d{2}-\d{3}$/.test(form.zip)) e.zip = 'Kod pocztowy w formacie 00-000.';
    if (form.city.trim().length < 2) e.city = 'Podaj miejscowość.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    setBusy(true);
    leaving.current = true;

    const order: Order = {
      id: `NF-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      createdAt: new Date().toISOString(),
      lines: cart.lines,
      shippingMethod: ship.label,
      shippingCost,
      paymentMethod: pay.label,
      coupon: cart.coupon,
      discount: cart.discount,
      tax: cart.tax,
      total,
      email: form.email,
      refunded: {},
    };

    cart.saveOrder(order);

    // purchase — rekomendowane zdarzenie GA4. `order.lines` to snapshot
    // sprzed cart.clear() niżej, więc bezpiecznie mapuje się przez
    // cartLineToItem nawet po tym, jak koszyk zostanie wyczyszczony.
    // transaction_id = id zamówienia — jedyny stabilny, unikalny
    // identyfikator, jaki tu mamy. `value` to order.total: kwota faktycznie
    // zapłacona (brutto, po rabacie, z dostawą) — dokładnie to, co widać
    // na ekranie potwierdzenia jako "Zapłacono". `tax` to rozbita składowa
    // VAT zawarta W total (ceny w tym sklepie są brutto), nie doliczana
    // osobno — zgodnie z tym, jak liczy to store/cart.tsx.
    const purchaseItems = order.lines.map(cartLineToItem).filter((item): item is NonNullable<typeof item> => item !== null);
    if (purchaseItems.length > 0) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: order.id,
          currency: CURRENCY,
          value: order.total,
          tax: order.tax,
          shipping: order.shippingCost,
          ...(order.coupon ? { coupon: order.coupon } : {}),
          items: purchaseItems,
        },
      });
    }

    fetch('/api/order', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: order.id, total: order.total, email: order.email }),
    }).catch(() => {
      /* sklep demonstracyjny działa też bez backendu */
    });

    cart.clear();
    cart.applyCoupon(undefined);

    // Płatności obsługuje zewnętrzny operator — pełne przeładowanie strony.
    if (pay.id === 'cod') {
      window.location.href = `/potwierdzenie/${order.id}`;
    } else {
      window.location.href = `/platnosc?zamowienie=${order.id}&kwota=${order.total}&metoda=${pay.id}`;
    }
  };

  const field = (key: keyof typeof form, label: string, placeholder: string, type = 'text') => (
    <div>
      <label className="mb-1 block text-xs text-slate-400" htmlFor={key}>
        {label}
      </label>
      <input
        id={key}
        type={type}
        className={inputCls}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
      />
      {errors[key] && <p className="mt-1 text-[11.5px] text-[#ffb0bb]">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
      <div>
        <div className="mb-5 flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold ${
                  step > i + 1
                    ? 'bg-[#22d3a6] text-[#0b1020]'
                    : step === i + 1
                      ? 'bg-[#4f8cff] text-white'
                      : 'bg-[#26304d] text-slate-400'
                }`}
              >
                {step > i + 1 ? '✓' : i + 1}
              </span>
              <span className={`text-[13px] ${step === i + 1 ? 'text-white' : 'text-slate-500'}`}>{s}</span>
              {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-[#26304d]" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold text-white">Dane do wysyłki</h2>
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!validate()) return;
                setStep(2);

                // add_shipping_info — rekomendowane zdarzenie GA4, gdy dane
                // adresowe przejdą walidację i wybrany jest sposób dostawy.
                // shipping_tier = nazwa wybranej metody, jedyny parametr
                // specyficzny dla tego zdarzenia (poza wspólnym szkieletem).
                const ecommerce = buildCheckoutEcommerce(cart.lines, cart.subtotal, cart.coupon);
                if (ecommerce) {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ ecommerce: null });
                  window.dataLayer.push({
                    event: 'add_shipping_info',
                    ecommerce: { ...ecommerce, shipping_tier: ship.label },
                  });
                }
              }}
            >
              {field('email', 'E-mail', 'jan@example.com', 'email')}
              {field('name', 'Imię i nazwisko', 'Jan Kowalski')}
              {field('street', 'Ulica i numer', 'Przykładowa 12/3')}
              <div className="grid grid-cols-[120px_1fr] gap-3">
                {field('zip', 'Kod pocztowy', '00-001')}
                {field('city', 'Miejscowość', 'Warszawa')}
              </div>

              <h3 className="pt-2 text-sm font-semibold text-white">Sposób dostawy</h3>
              <div className="space-y-2">
                {SHIPPING_METHODS.map((s) => (
                  <label
                    key={s.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 ${
                      ship.id === s.id ? 'border-[#4f8cff] bg-[#4f8cff]/10' : 'border-[#26304d]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="dostawa"
                      checked={ship.id === s.id}
                      onChange={() => setShip(s)}
                      className="accent-[#4f8cff]"
                    />
                    <span className="flex-1 text-[13.5px] text-slate-100">
                      {s.label}
                      <span className="ml-2 text-[11.5px] text-slate-500">{s.eta}</span>
                    </span>
                    <span className="text-[13px] text-slate-300">
                      {shippingCost === 0 && s.id === ship.id ? 'gratis' : s.price === 0 ? 'gratis' : <Money v={s.price} />}
                    </span>
                  </label>
                ))}
              </div>

              <Button type="submit" className="mt-2">
                Dalej
              </Button>
            </form>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold text-white">Sposób płatności</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {PAYMENT_METHODS.map((p) => (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 ${
                    pay.id === p.id ? 'border-[#4f8cff] bg-[#4f8cff]/10' : 'border-[#26304d]'
                  }`}
                >
                  <input
                    type="radio"
                    name="platnosc"
                    checked={pay.id === p.id}
                    onChange={() => setPay(p)}
                    className="accent-[#4f8cff]"
                  />
                  <span className="text-[13.5px] text-slate-100">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-slate-500">
              Płatności online obsługuje zewnętrzny operator. Po kliknięciu „Zamawiam" przejdziesz na stronę
              płatności, a po jej zakończeniu wrócisz do sklepu.
            </p>
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" onClick={() => setStep(1)}>
                Wstecz
              </Button>
              <Button
                onClick={() => {
                  setStep(3);

                  // add_payment_info — rekomendowane zdarzenie GA4, gdy
                  // sposób płatności jest wybrany i użytkownik idzie dalej.
                  // payment_type = nazwa wybranej metody.
                  const ecommerce = buildCheckoutEcommerce(cart.lines, cart.subtotal, cart.coupon);
                  if (ecommerce) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ ecommerce: null });
                    window.dataLayer.push({
                      event: 'add_payment_info',
                      ecommerce: { ...ecommerce, payment_type: pay.label },
                    });
                  }
                }}
              >
                Dalej
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-5">
            <h2 className="mb-3 text-base font-semibold text-white">Sprawdź zamówienie</h2>
            <dl className="space-y-1.5 text-[13px]">
              <div className="flex gap-2">
                <dt className="w-32 text-slate-500">Odbiorca</dt>
                <dd className="text-slate-200">
                  {form.name}, {form.street}, {form.zip} {form.city}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-slate-500">E-mail</dt>
                <dd className="text-slate-200">{form.email}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-slate-500">Dostawa</dt>
                <dd className="text-slate-200">{ship.label}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-32 text-slate-500">Płatność</dt>
                <dd className="text-slate-200">{pay.label}</dd>
              </div>
            </dl>

            <label className="mt-4 flex items-start gap-2 text-[12px] text-slate-400">
              <input type="checkbox" defaultChecked className="mt-0.5 h-4 w-4 accent-[#4f8cff]" />
              Akceptuję regulamin i politykę prywatności sklepu.
            </label>

            <div className="mt-4 flex gap-2">
              <Button variant="ghost" onClick={() => setStep(2)}>
                Wstecz
              </Button>
              <Button onClick={submit} disabled={busy}>
                {busy ? 'Przekierowuję…' : `Zamawiam i płacę · ${total.toFixed(2)} zł`}
              </Button>
            </div>
          </Card>
        )}
      </div>

      <Card className="h-fit p-4">
        <h2 className="mb-3 text-sm font-semibold text-white">Twoje zamówienie</h2>
        <ul className="space-y-2 text-[12.5px]">
          {cart.lines.map((l) => {
            const p = byId(l.productId);
            return (
              <li key={l.sku} className="flex justify-between gap-2">
                <span className="text-slate-400">
                  {l.qty} × {p?.name}
                </span>
                <span className="shrink-0 text-slate-200">
                  <Money v={unitPrice(l) * l.qty} />
                </span>
              </li>
            );
          })}
        </ul>
        <div className="my-3 border-t border-[#26304d]" />
        <div className="space-y-1 text-[12.5px]">
          {cart.discount > 0 && (
            <div className="flex justify-between">
              <span className="text-slate-400">Rabat</span>
              <span className="text-[#7ef0d0]">
                −<Money v={cart.discount} />
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-400">Dostawa</span>
            <span className="text-slate-200">{shippingCost === 0 ? 'gratis' : <Money v={shippingCost} />}</span>
          </div>
          <div className="flex justify-between pt-1.5 text-[14px] font-semibold text-white">
            <span>Razem</span>
            <Money v={total} />
          </div>
        </div>
      </Card>
    </div>
  );
}
